import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';
import { FormChangesService } from '@awp/forms';

export type StepState = 'none' | 'active' | 'completed' | string;

export enum StateChanged {
  VisibilityChange = 'visibilityChange',
  InfoPanelChange = 'infoPanelVisibilityChange',
  Completed = 'completed',
  Visited = 'visited',
  Submitted = 'submitted',
  Reset = 'reset',
  Active = 'active',
  NextVisible = 'next_visible',
  NextHidden = 'next_hidden',
  NextEnabled = 'next_enabled',
  NextDisabled = 'next_disabled',
}

export interface StateChange {
  state: StateChanged;
  id?: string;
}

@Injectable()
export class StepperStateService {
  protected _nextActivatableStep: string = null;
  protected _visibleSteps = new Set<string>();
  protected _visibleInfoSteps = new Set<string>();
  protected _completedSteps = new Set<string>();
  protected _visitedSteps = new Set<string>();
  protected _submittedSteps = new Set<string>();

  protected _stateChanges = new Subject<StateChange>();
  public stateChanges = this._stateChanges.asObservable()

  private _nextVisible = new BehaviorSubject<boolean>(true);
  public nextVisible = this._nextVisible.asObservable();

  private _nextEnabled = new BehaviorSubject<boolean>(true);
  public nextEnabled = this._nextEnabled.asObservable();
  public allowDisableNext = true;

  protected _activeStepId: string;
  protected _isVisible = true;
  protected _isNextEnabled = false;

  constructor(protected formChanges: FormChangesService) { }

  public reset(state: { completed?: string[], visited?: string[], submitted?: string[]} = { completed: [], visited: [], submitted: [] }, notify: boolean = true): void {
    this._nextActivatableStep = null;
    this._completedSteps = new Set<string>(state.completed || []);
    this._visitedSteps = new Set<string>(state.visited || []);
    this._submittedSteps = new Set<string>(state.submitted || []);
    if (notify) {
      this._stateChanges.next({state: StateChanged.Reset});
    }
  }

  markStepAsNextActivatable(id: string) {
    this._nextActivatableStep = id;
  }

  public markStepAsCompleted(id: string): void {
    this._completedSteps.add(id);
    this._stateChanges.next({ state: StateChanged.Completed, id });
  }

  public markStepAsVisited(id: string): void {
    this._visitedSteps.add(id);
    this._stateChanges.next({ state: StateChanged.Visited, id});
  }

  public markStepAsSubmitted(id: string): void {
    this._submittedSteps.add(id);
    this._stateChanges.next({ state: StateChanged.Submitted, id});
    this.formChanges.markAsTouched();
  }

  public isComplete(id: string): boolean {
    return this._completedSteps.has(id);
  }

  public hasSubmitted(id: string): boolean {
    return this._submittedSteps.has(id);
  }

  public hasVisited(id: string): boolean {
    return this._visitedSteps.has(id);
  }

  public changeActiveStep(id: string): void {
    this._activeStepId = id;
    this._stateChanges.next({ state: StateChanged.Active, id});
    if (this.hasSubmitted(id)) {
      this.formChanges.markAsTouched();
    }
  }

  get activeStepId(): string {
    return this._activeStepId;
  }

  get isNextVisible() {
    return this._isVisible;
  }

  public showNext(): void {
    this._isVisible = true;
    this._nextVisible.next(this._isVisible);
    this._stateChanges.next({ state: StateChanged.NextVisible });
  }

  public hideNext(): void {
    this._isVisible = false;
    this._nextVisible.next(this._isVisible);
    this._stateChanges.next({ state: StateChanged.NextHidden });
  }

  get isNextEnabled() {
    return this._isNextEnabled;
  }

  public enableNext(): void {
    this._isNextEnabled = true;
    this._nextEnabled.next(this._isNextEnabled);
    this._stateChanges.next({ state: StateChanged.NextEnabled });
  }

  public disableNext(): void {
    this._isNextEnabled = !this.allowDisableNext;
    this._nextEnabled.next(this._isNextEnabled);
    this._stateChanges.next({ state: StateChanged.NextDisabled});
  }

  public getStepState(id: string, activeStepId: string, activeParentStepId?: string): StepState {
    if (id === activeStepId || id === activeParentStepId) {
      return 'active';
    } else if (this.isComplete(id)) {
      return 'completed';
    } else {
      return 'none';
    }
  }

  public setVisibleSteps(ids: string[]): void {
    this._visibleSteps = new Set<string>(ids);
    this._stateChanges.next({ state: StateChanged.VisibilityChange });
  }

  public showStep(id: string): void {
    this._visibleSteps.add(id);
    this._stateChanges.next({ id, state: StateChanged.VisibilityChange });
  }

  public hideStep(id: string): void {
    this._visibleSteps.delete(id);
    this._stateChanges.next({ id, state: StateChanged.VisibilityChange });
  }

  public isStepVisible(id: string): boolean {
    return this._visibleSteps.has(id);
  }

  public hasVisibleSteps(): boolean {
    return this._visibleSteps.size > 0;
  }

  public showStepInfo(id: string): void {
    this._visibleSteps.add(id);
    this._stateChanges.next({ id, state: StateChanged.InfoPanelChange });
  }

  public hideStepInfo(id: string): void {
    this._visibleSteps.delete(id);
    this._stateChanges.next({ id, state: StateChanged.InfoPanelChange });
  }

  public isStepInfoVisible(id: string): boolean {
    return this._visibleInfoSteps.has(id);
  }

  public isMarkedAsNextActivatableStep(id: string): boolean {
    return this._nextActivatableStep === id;
  }
}
